import { useEffect, useState } from 'react';
import { 
  Card, 
  Form, 
  InputNumber, 
  Input, 
  Button, 
  Table, 
  Tag, 
  Space, 
  message, 
  Statistic, 
  Row, 
  Col,
  Alert,
  Typography,
  Modal,
  Tooltip,
  Divider
} from 'antd';
import { 
  DollarOutlined, 
  WarningOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  StopOutlined,
  BankOutlined
} from '@ant-design/icons';
import { PageHeader } from '../components/PageHeader';
import api, { WithdrawalRequest } from '../api';
import { paymentsEndpoints, type BankAccount } from '../api/paymentsEndpoints';
import { formatCurrency } from '../utils/formatters';

const { TextArea } = Input;
const { Text } = Typography;

export function Withdrawals() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [currentWithdrawableCents, setCurrentWithdrawableCents] = useState(0);
  const [defaultBankAccount, setDefaultBankAccount] = useState<BankAccount | null>(null);
  const [loadingBankAccount, setLoadingBankAccount] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  useEffect(() => {
    fetchWithdrawals();
    fetchDefaultBankAccount();
  }, []);

  const fetchWithdrawals = async (page = 1) => {
    try {
      setLoadingHistory(true);
      const data = await api.withdrawals.list({
        page,
        per: pagination.pageSize,
      });
      
      // Validate response structure
      if (!data || !data.withdrawals) {
        throw new Error('Invalid response structure from API');
      }
      
      setWithdrawals(data.withdrawals);
      setCurrentWithdrawableCents(data.current_withdrawable_cents || 0);
      setPagination({
        ...pagination,
        current: page,
        total: data.pagination?.total_count || 0,
      });
    } catch (error: any) {
      console.error('Failed to fetch withdrawals:', error);
      message.error('Failed to load withdrawal history');
      // Set defaults on error
      setWithdrawals([]);
      setCurrentWithdrawableCents(0);
    } finally {
      setLoadingHistory(false);
    }
  };

  // S-D1: Fetch default bank account
  const fetchDefaultBankAccount = async () => {
    try {
      setLoadingBankAccount(true);
      const response = await paymentsEndpoints.listBankAccounts();
      const defaultAccount = response.accounts.find(acc => acc.is_default && acc.status === 'active');
      setDefaultBankAccount(defaultAccount || null);
    } catch (error: any) {
      console.error('Failed to fetch bank accounts:', error);
      setDefaultBankAccount(null);
    } finally {
      setLoadingBankAccount(false);
    }
  };

  const handleSubmit = async (values: { amount_cents: number; user_note?: string }) => {
    try {
      setLoading(true);
      const result = await api.withdrawals.create({
        amount_cents: values.amount_cents,
        user_note: values.user_note,
      });

      if (result.success) {
        message.success(result.message || 'Withdrawal request submitted successfully');
        form.resetFields();
        fetchWithdrawals(); // Refresh list
      } else {
        message.error(result.errors?.join(', ') || 'Failed to submit withdrawal request');
      }
    } catch (error: any) {
      console.error('Failed to create withdrawal:', error);
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to submit withdrawal request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (withdrawal: WithdrawalRequest) => {
    Modal.confirm({
      title: 'Cancel Withdrawal Request',
      content: `Are you sure you want to cancel this ${formatCurrency(withdrawal.amount_cents)} withdrawal request?`,
      okText: 'Yes, Cancel',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const result = await api.withdrawals.cancel(withdrawal.id);
          if (result.success) {
            message.success(result.message || 'Withdrawal request canceled');
            fetchWithdrawals();
          } else {
            message.error(result.errors?.join(', ') || 'Failed to cancel withdrawal');
          }
        } catch (error: any) {
          console.error('Failed to cancel withdrawal:', error);
          message.error('Failed to cancel withdrawal request');
        }
      },
    });
  };

  const setPercentage = (percent: number) => {
    const amount = Math.floor((currentWithdrawableCents * percent) / 100);
    form.setFieldValue('amount_cents', amount);
  };

  const getStatusTag = (status: string) => {
    const config = {
      pending: { color: 'blue', icon: <ClockCircleOutlined />, text: 'Pending' },
      approved: { color: 'cyan', icon: <CheckCircleOutlined />, text: 'Approved' },
      rejected: { color: 'red', icon: <CloseCircleOutlined />, text: 'Rejected' },
      done: { color: 'green', icon: <CheckCircleOutlined />, text: 'Completed' },
      canceled: { color: 'default', icon: <StopOutlined />, text: 'Canceled' },
    };
    
    const { color, icon, text } = config[status as keyof typeof config] || config.pending;
    return <Tag color={color} icon={icon}>{text}</Tag>;
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'requested_at',
      key: 'requested_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Amount',
      dataIndex: 'amount_cents',
      key: 'amount_cents',
      render: (cents: number) => formatCurrency(cents),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Note',
      dataIndex: 'user_note',
      key: 'user_note',
      render: (note: string) => note || '-',
    },
    {
      title: 'Admin Note',
      dataIndex: 'admin_note',
      key: 'admin_note',
      render: (note: string) => note || '-',
    },
    {
      title: 'Resolved',
      dataIndex: 'resolved_at',
      key: 'resolved_at',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: WithdrawalRequest) => (
        record.status === 'pending' ? (
          <Button
            size="small"
            danger
            onClick={() => handleCancel(record)}
          >
            Cancel
          </Button>
        ) : null
      ),
    },
  ];

  const withdrawableAmount = currentWithdrawableCents / 100;
  const canWithdraw = currentWithdrawableCents > 0;

  return (
    <div>
      <PageHeader
        title="Withdrawal Requests"
        description="Request withdrawals from your available balance"
      />

      {/* Current Balance */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Current Withdrawable Balance"
              value={withdrawableAmount}
              precision={2}
              prefix={<DollarOutlined />}
              styles={{ content: { color: canWithdraw ? '#3f8600' : '#999' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Pending Requests"
              value={withdrawals.filter(w => w.status === 'pending').length}
              styles={{ content: { color: '#1677ff' } }}
            />
          </Card>
        </Col>
      </Row>

      {/* Withdrawal Request Form */}
      <Card 
        title="Request Withdrawal" 
        style={{ marginBottom: 24 }}
        extra={
          !canWithdraw && (
            <Tag icon={<WarningOutlined />} color="warning">
              No balance available
            </Tag>
          )
        }
      >
        {!canWithdraw ? (
          <Alert
            title="Withdrawals Not Available"
            description="You don't have any withdrawable balance at this time. Withdrawable balance comes from profit allocations that are not reinvested."
            type="info"
            showIcon
          />
        ) : (
          <>
            <Alert
              title="Withdrawal Process"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li>Withdrawal requests are processed manually by administrators</li>
                  <li>Processing typically takes 1-3 business days</li>
                  <li>You'll receive updates via email as your request progresses</li>
                  <li>You can cancel pending requests at any time</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              {/* S-D1: Bank Account Destination */}
              {loadingBankAccount ? (
                <Alert message="Loading bank account..." type="info" showIcon style={{ marginBottom: 16 }} />
              ) : !defaultBankAccount ? (
                <Alert
                  message="No Default Bank Account"
                  description={
                    <span>
                      You must set a default bank account before requesting a withdrawal.{' '}
                      <a href="/payments">Manage Bank Accounts</a>
                    </span>
                  }
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginBottom: 16 }}
                />
              ) : (
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f2f5' }}>
                  <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong>
                      <BankOutlined /> Payout Destination
                    </Text>
                    <div>
                      <Text>{defaultBankAccount.institution_name}</Text>
                      <br />
                      <Text type="secondary">
                        {defaultBankAccount.account_type} ••••{defaultBankAccount.mask}
                      </Text>
                      {defaultBankAccount.account_name && (
                        <>
                          <br />
                          <Text type="secondary">{defaultBankAccount.account_name}</Text>
                        </>
                      )}
                    </div>
                    <a href="/payments" style={{ fontSize: '12px' }}>
                      Change default bank account
                    </a>
                  </Space>
                </Card>
              )}

              <Form.Item
                label="Amount (cents)"
                name="amount_cents"
                rules={[
                  { required: true, message: 'Please enter an amount' },
                  { type: 'number', min: 1, message: 'Amount must be greater than 0' },
                  { 
                    type: 'number', 
                    max: currentWithdrawableCents, 
                    message: `Cannot exceed ${formatCurrency(currentWithdrawableCents)}` 
                  },
                ]}
                extra={
                  <Space style={{ marginTop: 8 }}>
                    <Text type="secondary">Quick select:</Text>
                    <Button size="small" onClick={() => setPercentage(25)}>25%</Button>
                    <Button size="small" onClick={() => setPercentage(50)}>50%</Button>
                    <Button size="small" onClick={() => setPercentage(75)}>75%</Button>
                    <Button size="small" onClick={() => setPercentage(100)}>100%</Button>
                  </Space>
                }
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter amount in cents"
                  min={1}
                  max={currentWithdrawableCents}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => Number(value?.replace(/\$\s?|(,*)/g, '')) || 0}
                  addonAfter={
                    <Tooltip title="Amount displayed in cents (100 cents = $1.00)">
                      cents
                    </Tooltip>
                  }
                />
              </Form.Item>

              <Form.Item
                label="Note (optional)"
                name="user_note"
              >
                <TextArea
                  rows={3}
                  placeholder="Add any notes or instructions for the admin..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={!defaultBankAccount}
                  icon={<DollarOutlined />}
                  size="large"
                  block
                >
                  Request Withdrawal
                </Button>
                {!defaultBankAccount && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 8 }}>
                    Set a default bank account to request withdrawals
                  </Text>
                )}
              </Form.Item>
            </Form>
          </>
        )}
      </Card>

      {/* Withdrawal History */}
      <Card title="Withdrawal History">
        <Table
          columns={columns}
          dataSource={withdrawals}
          rowKey="id"
          loading={loadingHistory}
          pagination={{
            ...pagination,
            onChange: fetchWithdrawals,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} requests`,
          }}
        />
      </Card>
    </div>
  );
}
